// src/components/assistant/ConsultCards.tsx
//
// 🤝 Tarjetas de consulta: lo que respondió cada agente que Orión consultó con la
// tool `consultar_agente` (Orion-Brainstream #424, services/agent_consult.py) cuando
// la persona escribió `@agente` en el mensaje.
//
// Forma de cada resultado (la de la tool, sin transformar):
//   { status: "ok", agente: {handle, nombre, icono}, respuesta: "markdown",
//     herramientas_usadas: [...], nota }
//   { status: "error", message, agente? }
//
// ⚠️ Hoy Orión NO manda el resultado de las tools al navegador: el stream solo emite
// la etiqueta legible («Consultando datos…») y el mensaje guardado no las trae. Estas
// tarjetas leen `message.consultas` (lista de resultados de arriba) si el backend la
// incluye; sin ese campo no se pinta nada y el chat se ve como siempre.
//
// Validación en el borde: lo que no tenga la forma esperada se descarta (no se
// inventa nombre ni @).

import { useId, useState } from 'react';
import { AlertCircle, Bot, ChevronDown, Users } from 'lucide-react';
import { MessageContent } from './MessageContent';

export interface Consulta {
  status: 'ok' | 'error';
  handle?: string;
  nombre?: string;
  icono?: string;
  respuesta?: string;
  message?: string;
  herramientas?: string[];
}

const HANDLE_RE = /^[a-z0-9]{2,60}$/;
const MAX_CONSULTAS = 12;
// Respuestas más largas que esto empiezan contraídas.
const LARGA = 420;

function str(v: unknown, max = 20000): string | undefined {
  return typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : undefined;
}

/** Normaliza la lista de resultados de `consultar_agente`; lo inválido se descarta. */
export function parseConsultas(raw: unknown): Consulta[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const out: Consulta[] = [];
  for (const r of raw.slice(0, MAX_CONSULTAS)) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const ag = o.agente && typeof o.agente === 'object' ? (o.agente as Record<string, unknown>) : {};
    const h = str(ag.handle, 60)?.replace(/^@/, '').toLowerCase();
    const handle = h && HANDLE_RE.test(h) ? h : undefined;
    const nombre = str(ag.nombre, 80);
    const icono = str(ag.icono, 8);
    if (o.status === 'ok') {
      const respuesta = str(o.respuesta);
      if (!respuesta || !(handle || nombre)) continue;
      const herramientas = Array.isArray(o.herramientas_usadas)
        ? o.herramientas_usadas.filter((x): x is string => typeof x === 'string').slice(0, 10)
        : undefined;
      out.push({ status: 'ok', handle, nombre, icono, respuesta, herramientas });
    } else if (o.status === 'error') {
      out.push({ status: 'error', handle, nombre, icono, message: str(o.message, 400) || 'No se pudo consultar al agente.' });
    }
  }
  return out.length ? out : undefined;
}

function ConsultCard({ c }: { c: Consulta }) {
  const larga = (c.respuesta?.length ?? 0) > LARGA;
  const [open, setOpen] = useState(!larga);
  const nombre = c.nombre || (c.handle ? `@${c.handle}` : 'Agente');
  const bodyId = useId();

  if (c.status === 'error') {
    return (
      <li className="flex min-w-0 items-start gap-2.5 rounded-2xl border border-dashed border-border bg-muted/30 px-3 py-2.5">
        <span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-muted text-base text-muted-foreground">
          {c.icono || <Bot size={15} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 text-[13px] font-medium text-foreground">
            <span className="truncate">{nombre}</span>
            {c.handle && c.nombre && <span className="text-xs font-normal text-muted-foreground">@{c.handle}</span>}
          </p>
          <p className="mt-0.5 flex items-start gap-1 text-xs text-muted-foreground">
            <AlertCircle size={12} aria-hidden className="mt-0.5 shrink-0" />
            <span className="min-w-0 break-words">{c.message}</span>
          </p>
        </div>
      </li>
    );
  }

  return (
    <li className="orion-card min-w-0 overflow-hidden rounded-2xl border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full min-w-0 items-center gap-2.5 px-3 py-2.5 text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      >
        <span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-primary/10 text-base text-primary">
          {c.icono || <Bot size={15} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-semibold text-foreground">{nombre}</span>
          {c.handle && <span className="block truncate text-xs text-primary">@{c.handle}</span>}
        </span>
        <span className="hidden shrink-0 text-[11px] text-muted-foreground sm:inline">{open ? 'Ocultar' : 'Ver opinión'}</span>
        <ChevronDown size={14} aria-hidden className={`shrink-0 text-muted-foreground motion-safe:transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div id={bodyId} hidden={!open} className="border-t border-border px-3 pb-3 pt-2 text-sm">
        <MessageContent text={c.respuesta || ''} />
        {c.herramientas && c.herramientas.length > 0 && (
          <p className="mt-2 text-[11px] text-muted-foreground">
            Usó {c.herramientas.length} {c.herramientas.length === 1 ? 'herramienta' : 'herramientas'} de solo lectura
          </p>
        )}
      </div>
      {!open && larga && (
        <p className="line-clamp-2 border-t border-border px-3 py-2 text-xs text-muted-foreground">{c.respuesta}</p>
      )}
    </li>
  );
}

/** Bloque «Orión consultó a…» con una tarjeta por agente. */
export function ConsultCards({ consultas }: { consultas: Consulta[] }) {
  if (!consultas.length) return null;
  const n = consultas.length;
  return (
    <section aria-label="Agentes consultados" className="-mx-1 mb-1 flex min-w-0 flex-col gap-2">
      <p className="flex items-center gap-1.5 px-1 text-xs font-medium text-muted-foreground">
        <Users size={13} aria-hidden className="text-primary" />
        Orión consultó a {n} {n === 1 ? 'agente' : 'agentes'}
      </p>
      <ul className="flex min-w-0 flex-col gap-2">
        {consultas.map((c, i) => (
          <ConsultCard key={`${c.handle || 'x'}-${i}`} c={c} />
        ))}
      </ul>
    </section>
  );
}
